import React, { useState, useEffect } from 'react';
import { Card, Tabs, Form, InputNumber, Button, Rate, Switch, Input, Select, Table, Tag, Row, Col, Statistic, message, Empty, Progress, Space } from 'antd';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import apiService from 'services/api/apiService';

const { TextArea } = Input;

interface KirkpatrickProps { programId: string; programTitle: string; enrollments: any[]; }

const RatingLabel = ({ score, max = 5 }: { score: number; max?: number }) => {
  const pct = (score / max) * 100;
  const color = pct >= 80 ? '#52c41a' : pct >= 60 ? '#faad14' : '#f5222d';
  return <Progress percent={pct} strokeColor={color} format={() => `${score}/${max}`} size="small" />;
};

const KirkpatrickEvaluation: React.FC<KirkpatrickProps> = ({ programId, programTitle, enrollments }) => {
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [l2form] = Form.useForm();
  const [l3form] = Form.useForm();
  const [l4form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('overview');
  const [hrDashboard, setHrDashboard] = useState<any>({});

  const fetchHRDashboard = async () => {
    const r = await apiService.getKirkpatrickHRDashboard(programId);
    if (r.success) setHrDashboard(r.data || {});
  };

  React.useEffect(() => { if (activeTab === 'hr_dashboard') fetchHRDashboard(); }, [activeTab]);

  useEffect(() => { if (programId) fetchSummary(); }, [programId]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const r = await apiService.getKirkpatrickSummary(programId);
      if (r.success) setSummary(r.data);
    } finally { setLoading(false); }
  };

  const submitL1 = async (values: any) => {
    const r = await apiService.saveKirkpatrickLevel(1, { ...values, program_id: programId, survey_date: new Date().toISOString().split('T')[0] });
    if (r.success) { message.success('Level 1 reaction saved'); form.resetFields(); fetchSummary(); }
    else message.error(r.error || 'Failed');
  };

  const submitL2 = async (values: any) => {
    const score = (values.questions_right / values.questions_total) * 100;
    const r = await apiService.saveKirkpatrickLevel(2, { ...values, program_id: programId, score: score.toFixed(2), passed: score >= (values.pass_mark || 70) ? 1 : 0, assessment_date: new Date().toISOString().split('T')[0] });
    if (r.success) { message.success('Level 2 assessment saved'); l2form.resetFields(); fetchSummary(); }
    else message.error(r.error || 'Failed');
  };

  const shareL3ToHR = async (values: any) => {
    const r = await apiService.shareEvaluation({ ...values, program_id: programId, level: 3, submitted_by: 'employee' });
    if (r.success) { message.success('L3 evaluation shared with HR successfully'); form.resetFields(); }
    else message.error(r.error || 'Failed to share evaluation');
  };

  const submitL3 = async (values: any) => {
    const r = await apiService.saveKirkpatrickLevel(3, { ...values, program_id: programId, observation_date: new Date().toISOString().split('T')[0] });
    if (r.success) { message.success('Level 3 observation saved'); l3form.resetFields(); fetchSummary(); }
    else message.error(r.error || 'Failed');
  };

  const submitL4 = async (values: any) => {
    const improvement = values.baseline_value && values.current_value
      ? (((values.current_value - values.baseline_value) / values.baseline_value) * 100).toFixed(2)
      : null;
    const r = await apiService.saveKirkpatrickLevel(4, { ...values, program_id: programId, improvement_pct: improvement, measurement_date: new Date().toISOString().split('T')[0] });
    if (r.success) { message.success('Level 4 result saved'); l4form.resetFields(); fetchSummary(); }
    else message.error(r.error || 'Failed');
  };

  const radarData = summary?.l1 ? [
    { subject: 'Relevance', value: summary.l1.avg_relevance || 0 },
    { subject: 'Content', value: summary.l1.avg_content || 0 },
    { subject: 'Trainer', value: summary.l1.avg_trainer || 0 },
    { subject: 'Overall', value: summary.l1.avg_overall || 0 },
  ] : [];

  const l2CompareData = summary?.l2 ? [
    { name: 'Pre-Training', score: parseFloat(summary.l2.pre?.avg_score || 0) },
    { name: 'Post-Training', score: parseFloat(summary.l2.post?.avg_score || 0) },
  ] : [];

  const tabItems = [
    {
      key: 'overview',
      label: '📊 Overview',
      children: (
        <div>
          {!summary ? <Empty description="No evaluation data yet" /> : (
            <>
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}><Card><Statistic title="L1 Responses" value={summary.l1?.responses || 0} /></Card></Col>
                <Col span={6}><Card><Statistic title="L1 Avg Rating" value={summary.l1?.avg_overall || 0} suffix="/5" precision={1} /></Card></Col>
                <Col span={6}><Card><Statistic title="L2 Post Score" value={summary.l2?.post?.avg_score || 0} suffix="%" precision={1} /></Card></Col>
                <Col span={6}><Card><Statistic title="Recommend %" value={summary.l1?.recommend_pct || 0} suffix="%" precision={0} /></Card></Col>
              </Row>
              <Row gutter={16}>
                {radarData.length > 0 && (
                  <Col span={12}>
                    <Card title="Level 1 — Reaction Scores">
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={radarData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" domain={[0, 5]} />
                          <YAxis type="category" dataKey="subject" width={80} />
                          <Tooltip formatter={(v: any) => `${v}/5`} />
                          <Bar dataKey="value" fill="#1890ff" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                )}
                {l2CompareData.some(d => d.score > 0) && (
                  <Col span={12}>
                    <Card title="Level 2 — Pre vs Post Assessment">
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={l2CompareData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis domain={[0, 100]} />
                          <Tooltip formatter={(v: any) => `${v}%`} />
                          <Bar dataKey="score" fill="#52c41a" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                )}
              </Row>
              {summary.l3?.length > 0 && (
                <Card title="Level 3 — Behaviour Change Over Time" style={{ marginTop: 16 }}>
                  <Table dataSource={summary.l3} rowKey="observation_period" pagination={false}
                    columns={[
                      { title: 'Period', dataIndex: 'observation_period', render: (v: string) => v.replace('_',' ') },
                      { title: 'Observations', dataIndex: 'observations' },
                      { title: 'Avg Skills Applied', dataIndex: 'avg_skills', render: (v: any) => <RatingLabel score={parseFloat(v||0)} /> },
                      { title: 'Avg Overall Change', dataIndex: 'avg_overall', render: (v: any) => <RatingLabel score={parseFloat(v||0)} /> },
                    ]} />
                </Card>
              )}
              {summary.l4?.length > 0 && (
                <Card title="Level 4 — Business Results" style={{ marginTop: 16 }}>
                  <Table dataSource={summary.l4} rowKey="metric_name" pagination={false}
                    columns={[
                      { title: 'Metric', dataIndex: 'metric_name' },
                      { title: 'Category', dataIndex: 'metric_category', render: (v: string) => <Tag>{v}</Tag> },
                      { title: 'Baseline', dataIndex: 'baseline_value', render: (v: any, r: any) => `${v} ${r.unit||''}` },
                      { title: 'Current', dataIndex: 'current_value', render: (v: any, r: any) => `${v} ${r.unit||''}` },
                      { title: 'Improvement', dataIndex: 'improvement_pct', render: (v: any) => v ? <Tag color={parseFloat(v)>=0?'success':'error'}>{v}%</Tag> : '-' },
                      { title: 'ROI Est.', dataIndex: 'roi_estimate', render: (v: any) => v ? `₦${parseFloat(v).toLocaleString()}` : '-' },
                    ]} />
                </Card>
              )}
            </>
          )}
        </div>
      )
    },
    {
      key: 'l1',
      label: '⭐ L1 Reaction',
      children: (
        <Card title="Level 1 — Reaction Survey (Post-Training)">
          <Form form={form} layout="vertical" onFinish={submitL1}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="employee_id" label="Employee" rules={[{required:true}]}>
                  <Select placeholder="Select employee" showSearch optionFilterProp="children">
                    {enrollments.map(e => <Select.Option key={e.employee_id} value={e.employee_id}>{e.employee_name}</Select.Option>)}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="enrollment_id" label="Enrollment" rules={[{required:true}]}>
                  <Select placeholder="Select enrollment">
                    {enrollments.map(e => <Select.Option key={e.id} value={e.id}>{e.employee_name} — {e.status}</Select.Option>)}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              {[['relevance','Content Relevance'],['content_quality','Content Quality'],['trainer_quality','Trainer Quality'],['materials','Training Materials'],['overall_rating','Overall Rating']].map(([name,label]) => (
                <Col span={8} key={name}>
                  <Form.Item name={name} label={label} rules={[{required:true}]}>
                    <InputNumber min={1} max={5} style={{width:'100%'}} addonAfter="/5" />
                  </Form.Item>
                </Col>
              ))}
            </Row>
            <Form.Item name="would_recommend" label="Would recommend to colleagues?" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
            <Form.Item name="key_learnings" label="Key Learnings"><TextArea rows={3} /></Form.Item>
            <Form.Item name="improvements" label="Suggested Improvements"><TextArea rows={3} /></Form.Item>
            <Form.Item name="comments" label="Additional Comments"><TextArea rows={2} /></Form.Item>
            <Button type="primary" htmlType="submit">Submit Reaction Survey</Button>
          </Form>
        </Card>
      )
    },
    {
      key: 'l2',
      label: '📝 L2 Learning',
      children: (
        <Card title="Level 2 — Learning Assessment (Pre & Post)">
          <Form form={l2form} layout="vertical" onFinish={submitL2} initialValues={{pass_mark:70,max_score:100}}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="employee_id" label="Employee" rules={[{required:true}]}>
                  <Select placeholder="Select employee">
                    {enrollments.map(e => <Select.Option key={e.employee_id} value={e.employee_id}>{e.employee_name}</Select.Option>)}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="enrollment_id" label="Enrollment" rules={[{required:true}]}>
                  <Select placeholder="Select enrollment">
                    {enrollments.map(e => <Select.Option key={e.id} value={e.id}>{e.employee_name}</Select.Option>)}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="assessment_type" label="Assessment Type" rules={[{required:true}]}>
                  <Select>
                    <Select.Option value="pre">Pre-Training</Select.Option>
                    <Select.Option value="post">Post-Training</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}><Form.Item name="questions_total" label="Total Questions" rules={[{required:true}]}><InputNumber min={1} style={{width:'100%'}} /></Form.Item></Col>
              <Col span={8}><Form.Item name="questions_right" label="Correct Answers" rules={[{required:true}]}><InputNumber min={0} style={{width:'100%'}} /></Form.Item></Col>
              <Col span={8}><Form.Item name="pass_mark" label="Pass Mark (%)"><InputNumber min={0} max={100} style={{width:'100%'}} /></Form.Item></Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}><Form.Item name="time_taken_mins" label="Time Taken (minutes)"><InputNumber min={0} style={{width:'100%'}} /></Form.Item></Col>
            </Row>
            <Form.Item name="notes" label="Notes"><TextArea rows={2} /></Form.Item>
            <Button type="primary" htmlType="submit">Save Assessment</Button>
          </Form>
        </Card>
      )
    },
    {
      key: 'l3',
      label: '🔍 L3 Behaviour',
      children: (
        <Card title="Level 3 — Behaviour Observation (Manager/Supervisor)">
          <Form form={l3form} layout="vertical" onFinish={submitL3}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="employee_id" label="Employee Being Observed" rules={[{required:true}]}>
                  <Select placeholder="Select employee">
                    {enrollments.map(e => <Select.Option key={e.employee_id} value={e.employee_id}>{e.employee_name}</Select.Option>)}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="observation_period" label="Observation Period" rules={[{required:true}]}>
                  <Select>
                    <Select.Option value="30_days">30 Days</Select.Option>
                    <Select.Option value="60_days">60 Days</Select.Option>
                    <Select.Option value="90_days">90 Days</Select.Option>
                    <Select.Option value="6_months">6 Months</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              {[['skills_applied','Skills Applied on Job'],['behaviour_change','Behaviour Change'],['knowledge_demonstrated','Knowledge Demonstrated'],['initiative','Initiative Shown'],['overall_change','Overall Change']].map(([name,label]) => (
                <Col span={8} key={name}>
                  <Form.Item name={name} label={label} rules={[{required:true}]}><InputNumber min={1} max={5} style={{width:'100%'}} addonAfter="/5" /></Form.Item>
                </Col>
              ))}
            </Row>
            <Form.Item name="observed_behaviours" label="Observed Behaviours"><TextArea rows={3} /></Form.Item>
            <Form.Item name="areas_for_improvement" label="Areas for Improvement"><TextArea rows={3} /></Form.Item>
            <Form.Item name="support_needed" label="Support Needed"><TextArea rows={2} /></Form.Item>
            <Button type="primary" htmlType="submit">Save Observation (HR Entry)</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => shareL3ToHR(l3form.getFieldsValue())}>
              📤 Share to HR (Self-Assessment)
            </Button>
          </Form>
        </Card>
      )
    },
    {
      key: 'l4',
      label: '📈 L4 Results',
      children: (
        <Card title="Level 4 — Business Results & ROI">
          <Form form={l4form} layout="vertical" onFinish={submitL4}>
            <Row gutter={16}>
              <Col span={12}><Form.Item name="metric_name" label="Metric Name" rules={[{required:true}]}><Input placeholder="e.g. Error Rate, Sales Revenue" /></Form.Item></Col>
              <Col span={12}>
                <Form.Item name="metric_category" label="Category" rules={[{required:true}]}>
                  <Select>
                    {['productivity','quality','safety','compliance','revenue','cost','other'].map(c => <Select.Option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</Select.Option>)}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={6}><Form.Item name="baseline_value" label="Baseline Value"><InputNumber style={{width:'100%'}} /></Form.Item></Col>
              <Col span={6}><Form.Item name="current_value" label="Current Value"><InputNumber style={{width:'100%'}} /></Form.Item></Col>
              <Col span={6}><Form.Item name="target_value" label="Target Value"><InputNumber style={{width:'100%'}} /></Form.Item></Col>
              <Col span={6}><Form.Item name="unit" label="Unit"><Input placeholder="%, ₦, hours, etc." /></Form.Item></Col>
            </Row>
            <Form.Item name="roi_estimate" label="ROI Estimate (₦)"><InputNumber style={{width:'100%'}} min={0} /></Form.Item>
            <Form.Item name="notes" label="Notes / Evidence"><TextArea rows={3} /></Form.Item>
            <Button type="primary" htmlType="submit">Save Business Result</Button>
          </Form>
        </Card>
      )
    },
    {
      key: 'hr_dashboard',
      label: '📊 HR Evaluation Dashboard',
      children: (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#666' }}>All evaluations submitted for this programme across all employees and all Kirkpatrick levels.</span>
            <Button size="small" onClick={fetchHRDashboard}>Refresh</Button>
          </div>
          {['L1','L2','L3','L4'].map(lvl => (
            <Card key={lvl} title={`Level ${lvl.replace('L','')} — ${['Reaction','Learning','Behaviour','Results'][parseInt(lvl.replace('L',''))-1]}`}
              size="small" style={{ marginBottom: 12 }}
              extra={<Tag color={['blue','green','purple','gold'][parseInt(lvl.replace('L',''))-1]}>{(hrDashboard[lvl]||[]).length} records</Tag>}>
              {(hrDashboard[lvl]||[]).length === 0
                ? <Empty description="No evaluations yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                : <Table size="small" dataSource={hrDashboard[lvl]||[]} rowKey="id" pagination={{ pageSize: 5 }}
                    columns={[
                      { title: 'Employee / Metric', key: 'emp', render: (_:any, r:any) => r.employee_name || r.metric_name || '—' },
                      { title: 'Programme', dataIndex: 'program_title', render: (v:string) => v || '—', ellipsis: true },
                      { title: 'Submitted By', dataIndex: 'submitted_by', render: (v:string) => <Tag color={v==='employee'?'blue':'green'}>{v||'hr'}</Tag> },
                      { title: 'Date', key: 'date', render: (_:any, r:any) => r.observation_date || r.assessment_date || r.survey_date || r.measurement_date || r.created_at?.split('T')[0] || '—' },
                      { title: 'Score/Rating', key: 'score', render: (_:any, r:any) => r.overall_change || r.score || r.overall_rating || r.improvement_pct || r.roi_estimate || '—' },
                    ]} />
              }
            </Card>
          ))}
        </div>
      )
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Kirkpatrick Evaluation — {programTitle}</h3>
        <p style={{ color: '#666', margin: '4px 0 0' }}>4-level training effectiveness framework</p>
      </div>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    </div>
  );
};

export default KirkpatrickEvaluation;
